import type { ProviderFigures } from '@/api/advertisingOverview'
import { Card, formatInteger, formatRatio } from '@/design'
import { ProviderAttributedBadge } from '@/features/analytics/advertising/AdvertisingBadges'
import { formatMoney, windowLabel } from '@/features/analytics/advertising/metrics'
import { CardQueryBody } from '@/features/overview/CardQueryBody'

import { UNDEFINED_FIGURE } from './definitions'
import { advertisingSectionError } from './errors'
import type { OverviewQueryLike } from './overviewQuery'
import { NotAvailable } from './SectionStates'

function ProviderFiguresList({ figures, currency }: { figures: ProviderFigures; currency: string }) {
  return (
    <dl className="aov-figures">
      <div>
        <dt>Purchases</dt>
        <dd>{formatInteger(figures.purchases)}</dd>
      </div>
      <div>
        <dt>Revenue</dt>
        <dd>{formatMoney(figures.revenueMinor, currency)}</dd>
      </div>
      <div>
        <dt>ROAS</dt>
        <dd>{figures.roas === null ? UNDEFINED_FIGURE : formatRatio(figures.roas)}</dd>
      </div>
      <div>
        <dt>CPA</dt>
        <dd>{figures.cpaMinor === null ? UNDEFINED_FIGURE : formatMoney(figures.cpaMinor, currency)}</dd>
      </div>
    </dl>
  )
}

/**
 * Meta's own purchases, revenue, ROAS and CPA for the same spend — shown beside the
 * KPI row's TmT figures, never added to them or substituted for them. The gap between
 * the two is the reconciliation panel's.
 */
export function ProviderReportedCard({ query }: { query: OverviewQueryLike }) {
  const data = query.data
  return (
    <Card
      title="Meta-reported results"
      subtitle="Meta’s own count under its attribution windows — beside TmT’s figures, never added to them"
      actions={data ? <ProviderAttributedBadge attributionWindows={data.meta.attributionWindows} /> : null}
      className="aov-provider"
    >
      <CardQueryBody query={query} loadingRows={3} errorDescription={advertisingSectionError(query.error, 'Meta’s reported figures could not be loaded.')}>
        {(overview) => {
          const { current, previous } = overview.providerReported
          if (!current) return <NotAvailable />
          return (
            <div className="aov-provider__body" data-source="meta">
              <ProviderFiguresList figures={current} currency={overview.meta.currency} />
              {previous ? (
                <p className="aov-note">
                  Previous period ({windowLabel(overview.meta.previousRange.from, overview.meta.previousRange.to)}):{' '}
                  {formatInteger(previous.purchases)} purchases, {formatMoney(previous.revenueMinor, overview.meta.currency)}{' '}
                  revenue.
                </p>
              ) : null}
            </div>
          )
        }}
      </CardQueryBody>
    </Card>
  )
}
